import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaCalendarAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import StatusChip from "../../../components/StatusChip/StatusChip";
import Loader from "../../../components/Loader/Loader";
import { useStudentAppointments } from "./hooks/useStudentAppointments";
import "./Appointments.css";

// Appointment fields aren't confirmed against a real payload yet - try a few
// reasonable field-name variants per value and fall back gracefully, same
// defensive style as StudentLeave.js / StudentCircular.js.
const pick = (item, keys, fallback = "") => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const getMeetingWith = (appointment) => {
  const direct = pick(appointment, ["meeting_with_type_name", "meeting_with", "type_name"]);
  if (direct) return direct;
  const nested = pick(appointment?.meeting_with_type, ["name"]);
  return nested || "Appointment";
};

// Only `4 = Cancelled` is actually confirmed (cancelAppointment's own
// payload, per the shipping mobile app's API client) - anything else is
// rendered as-is rather than guessed at.
const NUMERIC_STATUS_LABELS = { 4: "Cancelled" };

const getStatusText = (appointment) => {
  const raw = pick(appointment, ["status_label", "status", "appointment_status"], "Pending");
  if (typeof raw === "number" && NUMERIC_STATUS_LABELS[raw]) return NUMERIC_STATUS_LABELS[raw];
  return String(raw);
};

const getPurpose = (appointment) => pick(appointment, ["purpose", "agenda", "reason"]);
const getDuration = (appointment) => pick(appointment, ["duration", "duration_minutes"]);
const getMeetingDate = (appointment) => pick(appointment, ["meeting_date", "scheduled_at", "confirmed_date", "date"]);

// The backend's exact status spelling isn't pinned down, so map case
// insensitively and fall back to a neutral tone for anything unexpected -
// same approach as StudentLeave.js's STATUS_TONE map.
const STATUS_TONE_RULES = [
  ["confirm", "success"],
  ["await", "warning"],
  ["pending", "warning"],
  ["cancel", "danger"],
  ["reject", "danger"],
  ["reschedul", "info"],
];

const getStatusTone = (statusText) => {
  const text = statusText.toLowerCase();
  const rule = STATUS_TONE_RULES.find(([match]) => text.includes(match));
  return rule ? rule[1] : "secondary";
};

// Cancel only makes sense while the appointment is still pending/confirmed/
// awaiting/rescheduled - not once it's already cancelled, rejected or
// completed.
const isFinalStatus = (statusText) => {
  const text = statusText.toLowerCase();
  return text.includes("cancel") || text.includes("reject") || text.includes("complet");
};

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const StudentAppointments = () => {
  const navigate = useNavigate();
  const { appointments, isLoading, isFeatureUnavailable, cancellingId, cancel } = useStudentAppointments();
  // One row at a time isn't required by the design - any number of cards can
  // be expanded together, same pattern as StudentCircular.js.
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (key) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="student-appointments">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h5 className="sp-section-title mb-0">Appointments</h5>
        <Button icon={<FaPlus />} onClick={() => navigate("/student-portal/appointments/create")}>
          Create Appointment
        </Button>
      </div>

      {isLoading ? (
        <Loader />
      ) : isFeatureUnavailable ? (
        <div className="sp-empty-state">
          <FaCalendarAlt size={32} />
          <p className="mb-0">Appointments aren&apos;t available for your school yet.</p>
        </div>
      ) : appointments.length === 0 ? (
        <div className="sp-empty-state">
          <FaCalendarAlt size={32} />
          <p className="mb-0">You haven&apos;t booked any appointments yet.</p>
        </div>
      ) : (
        <div className="student-appointment-list">
          {appointments.map((appointment, index) => {
            const appointmentId = appointment?.id;
            const key = appointmentId ?? index;
            const meetingWith = getMeetingWith(appointment);
            const statusText = getStatusText(appointment);
            const tone = getStatusTone(statusText);
            const purpose = getPurpose(appointment);
            const duration = getDuration(appointment);
            const meetingDate = getMeetingDate(appointment);
            const isExpanded = expandedIds.has(key);
            const contextLine = purpose || (duration ? `Duration: ${duration}` : "");
            const canCancel = !isFinalStatus(statusText);

            return (
              <div key={key} className="sp-card student-appointment-card" data-tone={tone}>
                <button
                  type="button"
                  className="student-appointment-head"
                  onClick={() => toggleExpanded(key)}
                  aria-expanded={isExpanded}
                >
                  <span className="student-appointment-with">{meetingWith}</span>
                  <span className="student-appointment-head-right">
                    <StatusChip tone={tone}>{capitalize(statusText)}</StatusChip>
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </button>

                {contextLine && <p className="student-appointment-context text-muted mb-0">{contextLine}</p>}

                {isExpanded && (
                  <div className="student-appointment-details">
                    <hr className="student-appointment-divider" />

                    <div className="student-appointment-field">
                      <span className="student-appointment-field-label">Purpose</span>
                      <span className="student-appointment-field-value">{purpose || "-"}</span>
                    </div>

                    <div className="student-appointment-field-row">
                      <div className="student-appointment-field">
                        <span className="student-appointment-field-label">Duration</span>
                        <span className="student-appointment-field-value">{duration || "-"}</span>
                      </div>
                      <div className="student-appointment-field">
                        <span className="student-appointment-field-label">Meeting Date</span>
                        <span className="student-appointment-field-value">
                          {meetingDate ? formatDate(meetingDate) : "-"}
                        </span>
                      </div>
                    </div>

                    {canCancel && appointmentId && (
                      <div className="student-appointment-actions">
                        <Button
                          variant="danger"
                          size="sm"
                          loading={cancellingId === appointmentId}
                          onClick={() => cancel(appointmentId)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentAppointments;
